import { useContext, useEffect, useState, useRef } from "react";
import usePosOrders from "../../../services/Pos/usePosOrders";
import { GlobalContext } from "../../../Context/GlobalContext";
import { toast } from "react-toastify";

const keypad = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["0", "Clear", "⌫"],
];

export default function MobileNumberModal({
  open,
  onClose,
  onSubmit,
  orderType = "takeaway",
  initialMobile = "",
  initialName = "",
}) {
  const { getCustomerList, addCustomerAddress, setDefaultDeliveryAddress } =
    usePosOrders();

  const { userID } = useContext(GlobalContext);
  const inputRef = useRef(null);

  const getCustomerListRef = useRef(getCustomerList);
  const lastFetchedMobileRef = useRef("");
  const requestIdRef = useRef(0);
  getCustomerListRef.current = getCustomerList;

  const [mobile, setMobile] = useState("");
  const [customer, setCustomer] = useState(null);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [landmark, setLandmark] = useState("");

  const [loading, setLoading] = useState(false);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);

  const handleDefaultCustomer = () => {
    const defaultMobile = "9999999999";

    lastFetchedMobileRef.current = "";
    requestIdRef.current += 1;

    setCustomer(null);
    setName("");
    setAddress("");
    setCity("");
    setLandmark("");
    setShowAddAddressForm(false);

    setMobile(defaultMobile);
  };

  const normalizeOrderType = (type) => {
    const normalizedType = String(type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_-]/g, "");

    if (normalizedType === "takeway" || normalizedType === "takeaway") {
      return "takeaway";
    }

    if (normalizedType === "dinein" || normalizedType === "dining") {
      return "dining";
    }

    if (normalizedType === "homedelivery" || normalizedType === "delivery") {
      return "homedelivery";
    }

    return normalizedType || "takeaway";
  };

  const safeOrderType = normalizeOrderType(orderType);
  const isDining = safeOrderType === "dining";
  const isHomeDelivery = safeOrderType === "homedelivery";
  const isTakeaway = safeOrderType === "takeaway";

  useEffect(() => {
    if (!open) return;

    const mobileNumber = String(mobile || "")
      .replace(/\D/g, "")
      .slice(-10);

    if (mobileNumber.length !== 10) {
      lastFetchedMobileRef.current = "";
      requestIdRef.current += 1;

      setCustomer(null);
      setLoading(false);
      setShowAddAddressForm(false);
      return;
    }

    // Same number ke liye repeat call mat karo
    if (lastFetchedMobileRef.current === mobileNumber) {
      return;
    }

    lastFetchedMobileRef.current = mobileNumber;

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    const timer = setTimeout(async () => {
      try {
        setLoading(true);

        const customerList = await getCustomerListRef.current(mobileNumber);

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        if (!Array.isArray(customerList) || customerList.length === 0) {
          setCustomer(null);
          setName("");
          setAddress("");
          setCity("");
          setLandmark("");
          setShowAddAddressForm(false);
          return;
        }

        const data = customerList[0];

        const fetchedMobile = String(data?.contact_number || mobileNumber)
          .replace(/\D/g, "")
          .slice(-10);

        const addressList = Array.isArray(data?.address)
          ? data.address
              .map((item) => (typeof item === "string" ? item.trim() : item))
              .filter(Boolean)
          : [];

        const customerData = {
          name: data?.contact_name?.trim() || "Customer",

          mobile: fetchedMobile,

          defaultAddress: data?.defaultAddress?.trim() || "",

          addressList,
        };

        setCustomer(customerData);
        setName(customerData.name);
        setShowAddAddressForm(false);

        if (safeOrderType === "homedelivery") {
          setAddress(customerData.defaultAddress || "");
          setCity("");
          setLandmark("");
        }
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        console.error("Customer fetch error:", error);

        // Error ke baad same number dobara try ho sake
        lastFetchedMobileRef.current = "";

        setCustomer(null);
        setName("");
        setAddress("");
        setCity("");
        setLandmark("");
        setShowAddAddressForm(false);
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setLoading(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [open, mobile, safeOrderType]);

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1;
      lastFetchedMobileRef.current = "";
      return;
    }

    const normalizedMobile =
      typeof initialMobile === "object" ? initialMobile?.mobile : initialMobile;

    const initialDigits = String(normalizedMobile || "")
      .replace(/\D/g, "")
      .slice(-10);

    // Default customer number input mein show/fetch nahi hoga
    const digits = initialDigits === "9999999999" ? "" : initialDigits;

    lastFetchedMobileRef.current = "";
    requestIdRef.current += 1;

    setMobile(digits);
    setName("");
    setCustomer(null);
    setAddress("");
    setCity("");
    setLandmark("");
    setLoading(false);
    setShowAddAddressForm(false);

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    if (customer && safeOrderType === "homedelivery") {
      setName(customer.name || "");

      const firstAddress = customer.addressList?.[0];

      if (firstAddress && typeof firstAddress === "object") {
        setAddress(firstAddress.address || "");
        setCity(firstAddress.city || "");
        setLandmark(firstAddress.landmark_area || "");
      } else if (customer.defaultAddress) {
        setAddress(customer.defaultAddress);
      }
    }
  }, [customer, safeOrderType]);

  if (!open) return null;

  //  KEYPAD
  const handleKeyClick = (key) => {
    if (key === "Clear") {
      lastFetchedMobileRef.current = "";
      requestIdRef.current += 1;
      setCustomer(null);
      setMobile("");
      return;
    }

    if (key === "⌫") {
      lastFetchedMobileRef.current = "";
      requestIdRef.current += 1;
      setCustomer(null);
      setMobile((prev) => prev.slice(0, -1));
      return;
    }

    if (/^\d$/.test(key) && mobile.length < 10) {
      lastFetchedMobileRef.current = "";
      requestIdRef.current += 1;
      setCustomer(null);
      setMobile((prev) => prev + key);
    }
  };

  const mobileDigits = String(mobile || "").replace(/\D/g, "");

  const isValidMobile = mobileDigits.length === 10;

  const isHomeDeliveryValid =
    isValidMobile &&
    (customer ||
      (name.trim() && address.trim() && city.trim() && landmark.trim()));

  const canContinue = isHomeDelivery ? isHomeDeliveryValid : true;

  const handleSubmit = () => {
    const finalMobile = String(customer?.mobile || mobile || "")
      .replace(/\D/g, "")
      .slice(-10);

    if (finalMobile.length !== 10) {
      toast.error("Enter valid mobile number");
      return;
    }

    if (
      isHomeDelivery &&
      !customer &&
      (!name.trim() || !address.trim() || !city.trim() || !landmark.trim())
    ) {
      toast.error("Please fill all address details for home delivery");
      return;
    }

    onSubmit({
      mobile: finalMobile,
      name: name.trim() || customer?.name || "Customer",
      isDefaultCustomer: finalMobile === "9999999999",
      isNewCustomer: !customer,
      address: customer?.defaultAddress || address || "",
      city: city || "",
      landmark: landmark || "",
    });

    onClose();
  };

  //  SAVE ADDRESS
  const handleSave = async () => {
    const finalMobile = String(mobile || "")
      .replace(/\D/g, "")
      .slice(-10);

    if (finalMobile.length !== 10) {
      toast.error("Enter valid mobile number");
      return;
    }

    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }

    if (!city.trim()) {
      toast.error("City is required");
      return;
    }

    if (!landmark.trim()) {
      toast.error("Landmark is required");
      return;
    }

    const payload = {
      userid: userID,
      custMobile: finalMobile,
      custName: name.trim() || customer?.name || "Customer",

      address: {
        address: address.trim(),
        city: city.trim(),
        landmark_area: landmark.trim(),
      },
    };

    try {
      const saved = await addCustomerAddress(payload);

      if (!saved) {
        toast.error("Address could not be added");
        return;
      }

      const updated = await getCustomerListRef.current(finalMobile);

      if (Array.isArray(updated) && updated.length > 0) {
        const data = updated[0];

        const updatedCustomer = {
          name: data?.contact_name?.trim() || name.trim() || "Customer",

          mobile: String(data?.contact_number || finalMobile)
            .replace(/\D/g, "")
            .slice(-10),

          defaultAddress: data?.defaultAddress?.trim() || "",

          addressList: Array.isArray(data?.address)
            ? data.address
                .map((item) => (typeof item === "string" ? item.trim() : item))
                .filter(Boolean)
            : [],
        };

        setCustomer(updatedCustomer);
        setName(updatedCustomer.name);
        setAddress("");
        setCity("");
        setLandmark("");
        setShowAddAddressForm(false);

        lastFetchedMobileRef.current = finalMobile;

        toast.success("Address added successfully");
      }
    } catch (error) {
      console.error("Add address error:", error);
      toast.error("Error while adding address");
    }
  };

  //  SET DEFAULT
  const handleDefaultAddress = async (addr) => {
    try {
      const trimmedAddr = addr.trim();

      setCustomer((prev) => ({
        ...prev,
        defaultAddress: trimmedAddr,
      }));

      await setDefaultDeliveryAddress({
        address: trimmedAddr,
        custMobile: mobile,
      });

      await new Promise((res) => setTimeout(res, 800));
      const updated = await getCustomerList(mobile);

      if (updated?.length > 0) {
        const data = updated[0];

        const backendDefault = data.defaultAddress?.trim();
        const finalDefault =
          backendDefault === trimmedAddr ? backendDefault : trimmedAddr;

        setCustomer({
          name: data.contact_name,
          mobile: data.contact_number,
          defaultAddress: finalDefault,
          addressList: data.address || [],
        });
      }
    } catch (err) {
      console.error("Default error:", err);
    }
  };

  return (
    <>
  
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1"
        onClick={onClose}
      >
        <div
          className="bg-white w-full max-w-6xl rounded-xl shadow-2xl p-6 montserrat-font-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-2 gap-6 montserrat-font-1">
            {/* LEFT SIDE */}
            <div>
              <div className="flex justify-between items-center mb-4 gap-3">
                <h2 className="font-semibold">Enter Mobile Number</h2>

                <div className="flex items-center gap-2">
                  {(isTakeaway || isDining) && (
                    <button
                      type="button"
                      onClick={handleDefaultCustomer}
                      className="bg-yellow-400 hover:bg-yellow-500 px-4 py-2 rounded"
                    >
                      Default
                    </button>
                  )}
                </div>
              </div>

              <input
                ref={inputRef}
                value={mobile}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 10);

                  if (digits !== mobile) {
                    lastFetchedMobileRef.current = "";
                    requestIdRef.current += 1;
                    setCustomer(null);
                  }

                  setMobile(digits);
                }}
                placeholder="Enter mobile number"
                autoFocus
                className="w-full border p-3 mb-5 text-center"
              />

              {/* KEYPAD */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {keypad.flat().map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyClick(key)}
                    className="h-16 bg-gray-100 rounded"
                  >
                    {key}
                  </button>
                ))}
              </div>

              <div className="flex gap-4">
                <button onClick={onClose} className="w-1/3 border p-3">
                  Back
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!canContinue}
                  className={`w-2/3 p-3 text-white ${
                    canContinue
                      ? "bg-green-500"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="bg-gray-100 p-4 rounded max-h-[500px] overflow-y-auto montserrat-font-1">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="font-semibold text-gray-700">
                  {isHomeDelivery ? "Delivery Address" : "Customer Details"}
                </h3>

                <button
                  type="button"
                  onClick={() => {
                    if (!isValidMobile) {
                      toast.error("Enter valid mobile number first");
                      return;
                    }

                    setName(customer?.name || name || "");
                    setAddress("");
                    setCity("");
                    setLandmark("");
                    setShowAddAddressForm(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
                >
                  Add Address
                </button>
              </div>

              {loading && (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin h-8 w-8 border-2 border-gray-400 border-t-transparent rounded-full" />
                </div>
              )}

              {!loading && showAddAddressForm && mobile.length === 10 && (
                <div className="mt-4 bg-white p-4 rounded shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-lg font-semibold text-gray-700">
                      Add New Address
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        setAddress("");
                        setCity("");
                        setLandmark("");
                        setShowAddAddressForm(false);
                      }}
                      className="text-xs text-red-500 hover:text-red-600"
                    >
                      Cancel
                    </button>
                  </div>

                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter Name"
                    className="w-full border p-2 mb-3"
                  />

                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter Address"
                    className="w-full border p-2 mb-3"
                  />

                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter City"
                    className="w-full border p-2 mb-3"
                  />

                  <input
                    type="text"
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                    placeholder="Landmark"
                    className="w-full border p-2 mb-4"
                  />

                  <button
                    onClick={handleSave}
                    disabled={
                      !name.trim() ||
                      !address.trim() ||
                      !city.trim() ||
                      !landmark.trim()
                    }
                    className={`w-full text-white py-2 rounded ${
                      name.trim() &&
                      address.trim() &&
                      city.trim() &&
                      landmark.trim()
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Save Address
                  </button>
                </div>
              )}

              {!loading &&
                mobile.length === 10 &&
                !customer &&
                !showAddAddressForm &&
                (isHomeDelivery || isTakeaway) && (
                  <div className="mt-4 bg-white p-4 rounded shadow-sm">
                    <p className="text-lg font-semibold text-gray-700 mb-2">
                      No Customer Found
                    </p>

                    <p className="text-sm text-gray-500 mb-4">
                      {isHomeDelivery
                        ? "Contact details are required for home delivery"
                        : "Add customer details optional for takeaway"}
                    </p>

                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter Name"
                      className="w-full border p-2 mb-3"
                    />

                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter Address"
                      className="w-full border p-2 mb-3"
                    />

                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Enter City"
                      className="w-full border p-2 mb-3"
                    />

                    <input
                      type="text"
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                      placeholder="Landmark (optional)"
                      className="w-full border p-2 mb-4"
                    />

                    <button
                      onClick={handleSave}
                      disabled={
                        !name.trim() ||
                        !address.trim() ||
                        !city.trim() ||
                        !landmark.trim()
                      }
                      className={`w-full text-white py-2 rounded ${
                        name.trim() &&
                        address.trim() &&
                        city.trim() &&
                        landmark.trim()
                          ? "bg-blue-500"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Save Address
                    </button>
                  </div>
                )}

              {/* ================= DINE-IN ================= */}
              {isDining && (
                <div className="bg-white border rounded-lg p-4 shadow-sm">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin h-8 w-8 border-2 border-gray-400 border-t-transparent rounded-full" />
                    </div>
                  ) : mobileDigits.length !== 10 ? (
                    <p className="text-sm text-gray-500">
                      Enter a 10-digit mobile number to fetch customer details.
                    </p>
                  ) : customer ? (
                    <>
                      <p className="text-sm text-green-600 font-semibold mb-3">
                        Customer Found
                      </p>

                      {/* CUSTOMER NAME AND MOBILE */}
                      <div className="border rounded p-3 mb-3 bg-green-50">
                        <p>
                          <b>Name:</b> {customer.name || "Customer"}
                        </p>

                        <p>
                          <b>Mobile:</b> {customer.mobile || mobileDigits}
                        </p>
                      </div>

                      {/* DEFAULT ADDRESS */}
                      {customer.defaultAddress && (
                        <div className="border rounded-lg p-3 mb-3 bg-gray-50">
                          <p className="text-xs font-semibold text-green-600 mb-1">
                            Default Address
                          </p>

                          <p className="text-sm break-words">
                            {customer.defaultAddress}
                          </p>
                        </div>
                      )}

                      {/* OTHER ADDRESSES */}
                      {Array.isArray(customer.addressList) &&
                        customer.addressList.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                              Other Addresses
                            </p>

                            {customer.addressList.map((addr, index) => {
                              const addressText =
                                typeof addr === "string"
                                  ? addr.trim()
                                  : addr?.address?.trim() || "";

                              if (!addressText) return null;

                              const normalizedAddress = addressText
                                .toLowerCase()
                                .replace(/\s+/g, " ")
                                .trim();

                              const normalizedDefault = String(
                                customer.defaultAddress || "",
                              )
                                .toLowerCase()
                                .replace(/\s+/g, " ")
                                .trim();

                              // Default address ko duplicate show nahi karna
                              if (normalizedAddress === normalizedDefault) {
                                return null;
                              }

                              return (
                                <div
                                  key={`${addressText}-${index}`}
                                  className="border rounded-lg p-3 mb-2 bg-gray-50"
                                >
                                  <p className="text-sm break-words">
                                    {addressText}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                      {/* NAME INPUT */}
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter Customer Name"
                        className="w-full border p-3"
                      />
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-semibold text-gray-700 mb-2">
                        No Customer Found
                      </p>

                      <p className="text-sm text-gray-500 mb-4">
                        Enter customer name to continue.
                      </p>

                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter Customer Name"
                        className="w-full border p-3"
                      />
                    </>
                  )}
                </div>
              )}

              {/* ================= TAKEAWAY  ================= */}
              {(safeOrderType === "takeaway" ||
                safeOrderType === "homedelivery") &&
                mobileDigits.length === 10 &&
                customer && (
                  <div className="border rounded-lg p-4 bg-white shadow-sm montserrat-font-1">
                    {/* HEADER */}
                    <h3 className="font-semibold mb-3">
                      {customer.name} | {customer.mobile}
                    </h3>

                    {/* DEFAULT ADDRESS */}
                    {customer.defaultAddress && (
                      <div className="border rounded-lg p-3 mb-4 bg-gray-50 flex items-center justify-between">
                        <div>
                          <span className="bg-green-500 text-white text-xs px-3 py-1 rounded mr-2">
                            Default Address
                          </span>
                          <span className="text-sm">
                            {customer.defaultAddress}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* OTHER ADDRESSES */}
                    {customer.addressList?.map((addr, i) => {
                      const normalize = (str) =>
                        str?.toLowerCase().replace(/\s+/g, " ").trim();

                      const isDefault =
                        normalize(addr) === normalize(customer.defaultAddress);

                      if (isDefault) return null; //  don't repeat default

                      return (
                        <div
                          key={i}
                          className="border rounded-lg p-3 mb-3 flex justify-between items-start bg-gray-50 gap-3 montserrat-font-1"
                        >
                          <span className="text-sm flex-1 leading-snug break-words">
                            {addr}
                          </span>

                          <button
                            onClick={() => handleDefaultAddress(addr)}
                            className="text-xs border border-green-500 text-green-600 px-3 py-1 rounded hover:bg-green-50"
                          >
                            Mark as Default
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
